'use es6';

import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

var _FILTER_TYPE_TEXT_LOO;

import React from 'react';
import { compose } from 'underscore';
import I18n from 'I18n';
import Immutable from 'immutable';
import { parse as parseParams, stringify as stringifyParams } from 'hub-http/helpers/params';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import GoodFiltersAndPage from '../constants/GoodFiltersAndPage';
import GoodSubTypes from '../constants/GoodSubTypes';
import SearchOptions from '../constants/SearchOptions';
import { getCustomModulePurposeOptionText, getGoodSubtypeText, getTemplatePurposeOptionText } from './langUtils';
import { areAllSidebarFiltersAtInitialValue } from '../utils/utils';
var FILTER_OPTION_PREFIX = '__FILTER__:';
var MINIMUM_CHARACTERS_FOR_SIDEBAR_OPTION = 3;
var FILTER_TYPE_TEXT_LOOKUP = (_FILTER_TYPE_TEXT_LOO = {}, _defineProperty(_FILTER_TYPE_TEXT_LOO, GoodFiltersAndPage.MODULE_PURPOSE, getCustomModulePurposeOptionText), _defineProperty(_FILTER_TYPE_TEXT_LOO, GoodFiltersAndPage.SUB_TYPE, getGoodSubtypeText), _defineProperty(_FILTER_TYPE_TEXT_LOO, GoodFiltersAndPage.TEMPLATE_PURPOSE, getTemplatePurposeOptionText), _FILTER_TYPE_TEXT_LOO);
export function parseAutocompleteResults(results) {
  return results.map(function (result) {
    return {
      text: result.name,
      value: result.urlToken,
      help: getGoodSubtypeText(result.subType),
      subType: result.subType
    };
  });
}
export function populateAutocompleteResultsIndex(results) {
  var resultsIndex = {};
  results.forEach(function (result) {
    resultsIndex[result.urlToken] = true;
  });
  return resultsIndex;
}
export function getTrimmedSearchTerm(searchTerm) {
  return searchTerm ? searchTerm.trim() : '';
}
export function getFilterOptionValue(filters) {
  return "" + FILTER_OPTION_PREFIX + stringifyParams(filters);
}

function addProviderOption(sanitizedSearchTerm, providerNames, subType, nameAndSidebarFilterOptions) {
  var _getFilterOptionValue;

  if (sanitizedSearchTerm.length < MINIMUM_CHARACTERS_FOR_SIDEBAR_OPTION) {
    return nameAndSidebarFilterOptions;
  }

  var provider = providerNames.find(function (name) {
    return name.toLowerCase().indexOf(sanitizedSearchTerm) !== -1;
  });

  if (!provider) {
    return nameAndSidebarFilterOptions;
  }

  if (sanitizedSearchTerm.length === 3 && provider.toLowerCase().indexOf('hub') === 0) {
    provider = 'HubSpot';
  }

  return nameAndSidebarFilterOptions.push({
    dropdownText: React.createElement(FormattedHTMLMessage, {
      message: "header.autocomplete.providedBy",
      options: {
        providerName: provider
      }
    }),
    value: getFilterOptionValue((_getFilterOptionValue = {}, _defineProperty(_getFilterOptionValue, GoodFiltersAndPage.PROVIDER, provider), _defineProperty(_getFilterOptionValue, GoodFiltersAndPage.SUB_TYPE, subType || GoodSubTypes.ALL), _getFilterOptionValue))
  });
}

export function getSidebarFiltersFromOptionValue(filterOptionValue) {
  var parsedFilters = parseParams(filterOptionValue.replace(FILTER_OPTION_PREFIX, ''));
  var modulePurpose = parsedFilters[GoodFiltersAndPage.MODULE_PURPOSE];

  if (modulePurpose) {
    parsedFilters[GoodFiltersAndPage.MODULE_PURPOSE] = Immutable.List([modulePurpose]);
  }

  return parsedFilters;
}

function getTextForFilterOption(filterType, option) {
  return FILTER_TYPE_TEXT_LOOKUP[filterType](option);
}

function getSidebarFilterMatch(filterType, searchTerm) {
  return SearchOptions[filterType].find(function (option) {
    return getTextForFilterOption(filterType, option).toLowerCase().indexOf(searchTerm) !== -1;
  });
}

function addFilterMatchToResults(filterType, searchTerm, nameAndSidebarFilterOptions) {
  var sidebarFilterOption = getSidebarFilterMatch(filterType, searchTerm);

  if (!sidebarFilterOption) {
    return nameAndSidebarFilterOptions;
  }

  return nameAndSidebarFilterOptions.push({
    dropdownText: React.createElement("span", null, I18n.text('header.autocomplete.filteredBy'), ' ', React.createElement("strong", null, getTextForFilterOption(filterType, sidebarFilterOption))),
    value: getFilterOptionValue(_defineProperty({}, filterType, sidebarFilterOption))
  });
}

function addSubTypeOption(sanitizedSearchTerm, nameAndSidebarFilterOptions) {
  if (sanitizedSearchTerm.length < MINIMUM_CHARACTERS_FOR_SIDEBAR_OPTION) {
    return nameAndSidebarFilterOptions;
  }

  return addFilterMatchToResults(GoodFiltersAndPage.SUB_TYPE, sanitizedSearchTerm, nameAndSidebarFilterOptions);
}

function addFilterByNameOption(sanitizedSearchTerm, goodFilters, numAutocompleteGoodResults, nameAndSidebarFilterOptions) {
  if (numAutocompleteGoodResults <= 1 || !sanitizedSearchTerm) {
    return nameAndSidebarFilterOptions;
  }

  var leadingText = I18n.text('header.autocomplete.catchAllWithSearchTerm');

  if (!areAllSidebarFiltersAtInitialValue(goodFilters)) {
    leadingText = I18n.text('header.autocomplete.catchFilteredWithSearchTerm');
  }

  return nameAndSidebarFilterOptions.push({
    dropdownText: React.createElement("span", null, leadingText, " ", React.createElement("strong", null, sanitizedSearchTerm)),
    value: sanitizedSearchTerm
  });
}

function addCustomModulePurposeOption(sanitizedSearchTerm, subType, nameAndSidebarFilterOptions) {
  if (subType !== GoodSubTypes.CUSTOM_MODULE || sanitizedSearchTerm.length < MINIMUM_CHARACTERS_FOR_SIDEBAR_OPTION) {
    return nameAndSidebarFilterOptions;
  }

  return addFilterMatchToResults(GoodFiltersAndPage.MODULE_PURPOSE, sanitizedSearchTerm, nameAndSidebarFilterOptions);
}

function addTemplatePurposeOption(sanitizedSearchTerm, subType, nameAndSidebarFilterOptions) {
  if (subType === GoodSubTypes.ALL || subType === GoodSubTypes.PACKS || subType === GoodSubTypes.CUSTOM_MODULE || sanitizedSearchTerm.length < MINIMUM_CHARACTERS_FOR_SIDEBAR_OPTION) {
    return nameAndSidebarFilterOptions;
  }

  return addFilterMatchToResults(GoodFiltersAndPage.TEMPLATE_PURPOSE, sanitizedSearchTerm, nameAndSidebarFilterOptions);
}

export function getNameAndSidebarFilterOptions(searchTerm, goodFilters, allProviders, numAutocompleteGoodResults) {
  var sanitizedSearchTerm = getTrimmedSearchTerm(searchTerm).toLowerCase();
  var subType = goodFilters.get(GoodFiltersAndPage.SUB_TYPE);
  return compose(addCustomModulePurposeOption.bind(null, sanitizedSearchTerm, subType), addTemplatePurposeOption.bind(null, sanitizedSearchTerm, subType), addSubTypeOption.bind(null, sanitizedSearchTerm), addProviderOption.bind(null, sanitizedSearchTerm, allProviders, subType), addFilterByNameOption.bind(null, sanitizedSearchTerm, goodFilters, numAutocompleteGoodResults))(Immutable.List.of());
}
export function isSidebarFilterType(selectedValue) {
  return selectedValue.startsWith(FILTER_OPTION_PREFIX);
}